import { useState, useCallback } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle, Flag, Puzzle, Layers, Trash2, Clock } from 'lucide-react';
import { graphql, useMutation } from 'react-relay';
import { Button } from '@lecartte/shared-ui/components/Button';
import type { ReprocessPageMutation } from '@/__generated__/ReprocessPageMutation.graphql';

const REPROCESS_MUTATION = graphql`
  mutation ReprocessPageMutation {
    ReprocessCapabilities {
      summary {
        featureFlagsCreated
        featureFlagsExisting
        capabilitiesUpserted
        capabilitiesRemoved
        planCapabilitiesUpserted
      }
      error
    }
  }
`;

type Summary = {
  featureFlagsCreated: number;
  featureFlagsExisting: number;
  capabilitiesUpserted: number;
  capabilitiesRemoved: number;
  planCapabilitiesUpserted: number;
};

type MetricItem = {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: 'neutral' | 'created' | 'removed';
};

function MetricCard({ item }: { item: MetricItem }) {
  const toneClasses = {
    neutral:  'bg-slate-50 text-slate-600',
    created:  'bg-green-50 text-green-600',
    removed:  'bg-red-50 text-red-600',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
      <div className={`shrink-0 size-9 rounded-lg flex items-center justify-center ${toneClasses[item.tone]}`}>
        {item.icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 leading-none">{item.value}</p>
        <p className="text-xs text-slate-500 mt-1">{item.label}</p>
      </div>
    </div>
  );
}

function SummaryCard({ summary, durationMs }: { summary: Summary; durationMs: number }) {
  const items: MetricItem[] = [
    {
      label: summary.featureFlagsCreated === 1 ? 'Feature flag criada' : 'Feature flags criadas',
      value: summary.featureFlagsCreated,
      icon: <Flag size={18} />,
      tone: summary.featureFlagsCreated > 0 ? 'created' : 'neutral',
    },
    {
      label: summary.featureFlagsExisting === 1 ? 'Feature flag existente' : 'Feature flags existentes',
      value: summary.featureFlagsExisting,
      icon: <Flag size={18} />,
      tone: 'neutral',
    },
    {
      label: summary.capabilitiesUpserted === 1 ? 'Capability sincronizada' : 'Capabilities sincronizadas',
      value: summary.capabilitiesUpserted,
      icon: <Puzzle size={18} />,
      tone: 'neutral',
    },
    {
      label: summary.capabilitiesRemoved === 1 ? 'Capability removida' : 'Capabilities removidas',
      value: summary.capabilitiesRemoved,
      icon: <Trash2 size={18} />,
      tone: summary.capabilitiesRemoved > 0 ? 'removed' : 'neutral',
    },
    {
      label: summary.planCapabilitiesUpserted === 1 ? 'Plano sincronizado' : 'Planos sincronizados',
      value: summary.planCapabilitiesUpserted,
      icon: <Layers size={18} />,
      tone: 'neutral',
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle2 size={18} />
          <span className="font-semibold text-sm">Reprocessamento concluído</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Clock size={13} />
          <span>{durationMs}ms</span>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => (
          <MetricCard key={item.label} item={item} />
        ))}
      </div>
    </div>
  );
}

export function ReprocessPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [commit] = useMutation<ReprocessPageMutation>(REPROCESS_MUTATION);

  const handleReprocess = useCallback(() => {
    setBusy(true);
    setError(null);
    setSummary(null);

    const start = performance.now();
    commit({
      variables: {},
      onCompleted: (data) => {
        const duration = Math.round(performance.now() - start);
        setDurationMs(duration);
        const err = data?.ReprocessCapabilities?.error;
        if (err) {
          setError(err);
        } else if (data?.ReprocessCapabilities?.summary) {
          setSummary(data.ReprocessCapabilities.summary as Summary);
        }
        setBusy(false);
      },
      onError: (err) => {
        setError(err.message);
        setBusy(false);
      },
    });
  }, [commit]);

  return (
    <div className="flex flex-col gap-5 max-w-4xl">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h1 className="font-display font-bold text-2xl text-slate-900">Reprocessar</h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
          Sincronize feature flags, capabilities e valores de plano com a configuração atual do sistema.
          Útil após restaurar dumps antigos ou adicionar novas capabilities.
        </p>
        <Button
          type="button"
          disabled={busy}
          onClick={handleReprocess}
          className="mt-5"
        >
          <RefreshCw size={16} className={busy ? 'animate-spin' : ''} />
          {busy ? 'Reprocessando...' : 'Reprocessar capacidades'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3 text-red-700 text-sm">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {summary && <SummaryCard summary={summary} durationMs={durationMs} />}
    </div>
  );
}
